import { useCallback, useEffect, useRef, useState } from "react";
import {
  getUnreadNotificationsCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "modules/notifications/api/notificationService";
import type {
  NotificationItem,
  NotificationListResponse,
} from "modules/notifications/model/notificationTypes";

const POLL_INTERVAL_MS = 60_000;

interface UseNotificationsResult {
  items: NotificationItem[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  markAsRead: (notification: NotificationItem) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

export function useNotifications(): UseNotificationsResult {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef<boolean>(true);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const response = await getUnreadNotificationsCount();
      if (!isMountedRef.current) return;
      setUnreadCount(typeof response.count === "number" ? response.count : 0);
    } catch (err) {
      // Silencioso: evita ruido em log durante poll. Mantem o ultimo unreadCount.
    }
  }, []);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response: NotificationListResponse = await listNotifications(1, 20, false);
      if (!isMountedRef.current) return;
      const list = Array.isArray(response.items) ? response.items : [];
      setItems(list);
      setUnreadCount(list.filter((n) => !n.read).length);
    } catch (err) {
      if (!isMountedRef.current) return;
      setError(err instanceof Error ? err.message : "Erro ao listar notificacoes.");
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    void refreshUnreadCount();

    const handle = window.setInterval(() => {
      void refreshUnreadCount();
    }, POLL_INTERVAL_MS);

    return () => {
      isMountedRef.current = false;
      window.clearInterval(handle);
    };
  }, [refreshUnreadCount]);

  const markAsRead = useCallback(async (notification: NotificationItem) => {
    if (notification.read) return;

    setItems((prev) =>
      prev.map((n) => (n.id === notification.id ? { ...n, read: true, readAt: new Date().toISOString() } : n)),
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));

    try {
      await markNotificationRead(notification.id);
    } catch (err) {
      void refreshUnreadCount();
    }
  }, [refreshUnreadCount]);

  const markAllAsRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);

    try {
      await markAllNotificationsRead();
    } catch (err) {
      void refreshUnreadCount();
    }
  }, [refreshUnreadCount]);

  return {
    items,
    unreadCount,
    isLoading,
    error,
    refresh,
    markAsRead,
    markAllAsRead,
  };
}
